<template>
  <div id="agConnPage">
    <!-- loading spinner, no workspaces, or no connections -->
    <b-spinner v-if="isLoading" id="agConnPage-loadingSpinner"></b-spinner>
    <div id="agConnPage-noWss" v-else-if="workspaces.length == 0">
      <fa icon="briefcase" />
      <div>No workspaces have been created</div>
    </div>
    <div id="agConnPage-noConn" v-else-if="connections.length == 0">
      <fa icon="plug" />
      <div>No connections have been setup</div>
    </div>

    <!-- connection modal -->
    <ag-connection-modal
      ref="connectionModal"
      :connection="modalConnection"
      @reloadConnections="reloadConnections"
    ></ag-connection-modal>

    <!-- connection delete confirmation -->
    <b-modal
      ref="deleteModal"
      id="agConnPage-deleteModal"
      class="agModal"
      centered
      no-close-on-esc
      no-close-on-backdrop
      @keyup.enter.native="deleteConnButton_click">
      <template slot="modal-header">
        <h4 class="agModal-title danger">Delete Connection</h4>
      </template>
      Are you sure you want to delete connection
      <i>{{modalConnection.name}}</i>?
      <template slot="modal-footer" slot-scope="{cancel}">
        <b-button class="agModal-buttonLeft" @click="cancel">Cancel</b-button>
        <b-button
          class="agModal-buttonRight"
          variant="danger"
          @click="deleteConnButton_click"
        >Delete</b-button>
      </template>
    </b-modal>

    <!-- toolbar -->
    <ag-toolbar>
      <template slot="leftContent">
        <b-button
          class="agImageButton"
          :disabled="isReadonly || isLoading || workspaces.length == 0"
          v-b-tooltip.hover
          title="Add connection"
          @click="addConnButton_click">
          <fa icon="plus" />
        </b-button>
      </template>
      <template slot="rightContent">
        <div class="agInputGroup">
          <label>Workspace:</label>
          <b-form-select
            class="agLargeInput"
            v-model="workspace"
            :options="workspaceOptions"
            :disabled="isLoading || workspaces.length == 0"
          ></b-form-select>
        </div>
      </template>
    </ag-toolbar>

    <!-- connections -->
    <b-card id="agConnPage-conns" v-if="!isLoading && sortedConnections.length > 0" :key="reload">
      <draggable v-model="sortedConnections" handle=".card-header" ghost-class="agConn-ghost">
        <ag-connection
          v-for="connection in sortedConnections"
          :key="connection._id"
          :connection="connection"
          @editConnection="editConnection"
          @deleteConnection="deleteConnection"
          @reloadConnections="reloadConnections"
        ></ag-connection>
      </draggable>
    </b-card>
  </div>
</template>





<script>
// components
import Toolbar from "comp/ui/Toolbar";
import Connection from "comp/connection/Connection";
import ConnectionModal from "comp/connection/ConnectionModal";

// server

// modules
import Util from "src/util/util";
import Draggable from "vuedraggable";

export default {
  name: "ag-connections-page",
  components: {
    "ag-toolbar": Toolbar,
    "ag-connection": Connection,
    "ag-connection-modal": ConnectionModal,
    draggable: Draggable
  },
  data() {
    return {
      reload: 0,
      isDeleting: false,
      workspaceOptions: [],
      sortedConnections: [],
      modalConnection: {
        name: "",
        type: "esri",
        url: "",
        username: "",
        password: "",
        authUrl: ""
      }
    };
  },
  computed: {
    isLoading() {
      if (
        this.$store.state.connStore.isLoading ||
        this.$store.state.wsStore.isLoading ||
        this.isDeleting
      )
        return true;
      return false;
    },

    isReadonly() {
      if (this.user.role == "readonly") return true;
      return false;
    },

    user() {
      return this.$store.state.userStore.user;
    },

    workspace: {
      get() {
        return this.$store.state.wsStore.workspace._id;
      },
      set(value) {
        this.$store.dispatch("wsStore/setWorkspace", {
          workspace: this.workspaces.find(a => a._id == value)
        });
      }
    },

    workspaces() {
      return this.$store.getters["wsStore/userWorkspaces"];
    },

    connections() {
      return this.$store.state.connStore.connections;
    }
  },

  watch: {
    workspaces() {
      this.setWorkspaceOptions();
    },

    connections() {
      this.setSortedConnections();
    },

    sortedConnections(oldList, newList) {
      if (
        oldList.length > 0 &&
        newList.length > 0 &&
        JSON.stringify(oldList) != JSON.stringify(newList)
      )
        this.saveConnectionOrder();
    }
  },

  mounted() {
    this.setWorkspaceOptions();
    this.setSortedConnections();
  },

  methods: {
    // UI Events

    addConnButton_click() {
      this.resetModalConnection();
      this.$refs.connectionModal.show();
    },

    async deleteConnButton_click() {
      this.$refs.deleteModal.hide();
      this.isDeleting = true;
      await this.$store.dispatch("connStore/deleteConnection", {
        connection: this.modalConnection
      });
      this.isDeleting = false;
      this.resetModalConnection();
    },

    // emitted events

    editConnection(connection) {
      this.modalConnection = JSON.parse(JSON.stringify(connection));
      this.$refs.connectionModal.show();
    },

    deleteConnection(connection) {
      this.modalConnection = JSON.parse(JSON.stringify(connection));
      this.$refs.deleteModal.show();
    },

    reloadConnections() {
      this.setSortedConnections();
      this.reload++;
    },

    // other methods

    resetModalConnection() {
      this.modalConnection = {
        name: "",
        type: "esri",
        url: "",
        username: "",
        password: "",
        authUrl: ""
      };
    },

    setWorkspaceOptions() {
      this.workspaceOptions = [];
      for (var i = 0; i < this.workspaces.length; i++) {
        var workspace = this.workspaces[i];
        this.workspaceOptions.push({
          value: workspace._id,
          text: workspace.name
        });
      }

      // select from userParms or first option if none selected
      if (this.workspaces.length > 0 && !this.workspace) {
        if (this.user.parms.workspace)
          var workspace = this.workspaces.find(
            a => a._id == this.user.parms.workspace
          );
        else {
          var workspace = this.workspaces[0];
        }
        this.$store.dispatch("wsStore/setWorkspace", { workspace: workspace });
      }
    },

    setSortedConnections() {
      this.sortedConnections = [];
      if (this.connections.length == 0) {
        return;
      }

      var remaining = JSON.parse(JSON.stringify(this.connections));
      // sort by user parm
      if (this.user.parms.workspaces) {
        var workspace = this.user.parms.workspaces.find(a => a.workspace == this.workspace);
        if(workspace) {
          var order = workspace.connections;
          if (order) {
            for (var i = 0; i < order.length; i++) {
              var connection = remaining.find(a => a._id == order[i]);
              if (connection) {
                this.sortedConnections.push(connection);
                remaining = Util.removeFromArrayByKey(
                  remaining,
                  "_id",
                  connection._id
                );
              }
            }
          }
        }
      }

      // add remaining
      for (var i = 0; i < remaining.length; i++) {
        this.sortedConnections.unshift(remaining[i]);
      }
    },

    saveConnectionOrder() {
      if (!this.user.parms.workspaces) 
        this.user.parms.workspaces = [];
      
      if (this.workspace != "" && !!this.workspace) {
        var workspace = this.user.parms.workspaces.find(a => a.workspace == this.workspace);
        if(!workspace) {
          workspace = {
            workspace: this.workspace,
            workflows: [],
            connections: []
          };
          this.user.parms.workspaces.push(workspace);
        }
        
        workspace.connections = []; // clear order
        for (var i = 0; i < this.sortedConnections.length; i++) {
          var connection = this.sortedConnections[i];
          workspace.connections.push(
            connection._id
          );
        }

        this.$store.dispatch("userStore/updateUserParms", { user: this.user });
      }
    }
  }
};
</script>






<style scoped lang="css">
#agConnPage-conns {
  position: relative;
  height: calc(100vh - 150px);
  top: 65px;
  overflow: auto;
}

#agConnPage-loadingSpinner {
  position: absolute;
  width: 50px;
  height: 50px;
  left: 50%;
  top: 50%;
  margin-left: -20px;
  margin-top: -20px;
}

#agConnPage-noWss {
  position: absolute;
  left: 50%;
  top: 50%;
  margin-left: -277px;
  margin-top: -61px;
  font-size: 36px;
  color: lightgray;
}

#agConnPage-noWss svg {
  font-size: 60px;
}

#agConnPage-noConn {
  position: absolute;
  left: 50%;
  top: 50%;
  margin-left: -265px;
  margin-top: -61px;
  font-size: 36px;
  color: lightgray;
}

#agConnPage-noConn svg {
  font-size: 60px;
}
</style>



// WEBPACK FOOTER //
// src/components/connection/ConnectionsPage.vue